// Import from NPM
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader, Image, Button, Divider, Message } from "semantic-ui-react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useFeatures } from "flagged";
import localforage from "localforage";
import CryptoJS from "crypto-js";
import _ from "lodash";

// Import from Config
import { login } from "@reducers/authSlice";
import { apiV6, baseUrl } from "@api/apiV6";
import { Div } from "@components/Generics.react";
import { settings } from "@config/settings/app.settings";
import { devMode } from "@utilities/helpers";
import AuthLayout from "@layouts/AuthLayout.react";
import { logIt } from "@utilities/logHelpers";
import { useNavigate, useLocation } from "react-router-dom";

export default function AutoLogin({ team, token, setToken, branding, authPageLinks }) {
    const dispatch = useDispatch();
    const features = useFeatures();
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(true);
    const [retryCount, setRetryCount] = useState(0);

    const wPageHt = window.innerHeight - 100;
    const isPortrait = window.innerHeight > window.innerWidth;

    // Handle token from URL or storage
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlToken = params.get("token");
        const userStatus = params.get("userStatus");

        if (urlToken) {
            localStorage.setItem("token", urlToken);
            if (userStatus === "new") {
                navigate("/builder/registered", {
                    state: { fromAuth: true, isNewUser: true },
                    replace: true
                });
                return;
            }
            setToken(urlToken);
        } else {
            const savedToken = localStorage.getItem("token");
            if (savedToken) {
                setToken(savedToken);
            } else {
                navigate("/login", { replace: true });
            }
        }
    }, [location.search, navigate, setToken]);

    // Authenticate user with token
    const authenticateUser = useCallback(async (authToken) => {
        setIsProcessing(true);
        setError(null);

        try {
            const response = await axios.post(
                `${baseUrl}api/v3/auth/connect`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json"
                    },
                    timeout: 10000 // 10 second timeout
                }
            );

            let payload;
            try {
                // Handle encrypted response (legacy support)
                if (typeof response.data === "string") {
                    const decrypted = CryptoJS.AES.decrypt(response.data, "16t17t5m").toString(CryptoJS.enc.Utf8);
                    payload = JSON.parse(decrypted);
                } else {
                    payload = response.data;
                }
            } catch (e) {
                // Fallback to direct parsing
                payload = typeof response.data === "string"
                    ? JSON.parse(response.data)
                    : response.data;
            }

            // Handle new response format
            const userData = payload.data || payload;
            const responseToken = response.headers["access-token"] || payload.token;

            if (!userData || userData.error) {
                throw new Error(userData?.message || "Invalid authentication response");
            }

            // Adjust branding
            const brandingAdjustedUser = {
                ...userData,
                branding: userData.branding || {
                    colors: {
                        main: settings.colors?.main,
                        primary: settings.colors?.primary,
                        primaryText: settings.colors?.primaryText,
                    }
                }
            };

            if (team != null) {
                window.localStorage.setItem("team", brandingAdjustedUser.team);
            }

            // Dispatch login and reset API state
            await dispatch(apiV6.util.resetApiState());
            await dispatch(login({
                user: brandingAdjustedUser,
                token: responseToken
            }));

            // Preload essential data
            await Promise.allSettled([
                dispatch(apiV6.endpoints.getArticles.initiate()),
                dispatch(apiV6.endpoints.getCourseLibrary.initiate())
            ]);

            // Handle redirect safely
            let redirectPath = localStorage.getItem("redirectPath");
            if (redirectPath && redirectPath !== "/" && redirectPath !== "/logout" && redirectPath !== "/login") {
                redirectPath = `${devMode ? "" : settings.targetRoute.slice(0, -1)}${redirectPath}`;
            } else {
                redirectPath = "/";
            }
            localStorage.removeItem("redirectPath");

            navigate(redirectPath, { replace: true });
            // Push state again so components remount using the new token
            window.history.replaceState({}, document.title, redirectPath);

        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Authentication failed";
            setError(errorMessage);

            // Auto-retry for network errors
            if (err.code === 'NETWORK_ERROR' || err.code === 'ECONNABORTED') {
                if (retryCount < 3) {
                    setTimeout(() => {
                        setRetryCount(prev => prev + 1);
                        authenticateUser(authToken);
                    }, 2000 * (retryCount + 1));
                    return;
                }
            }

            localStorage.removeItem("token");
            logIt({
                page: "Login",
                problem: `Token Login failed`,
                criticality: 0,
                trace: errorMessage,
            });
        } finally {
            setIsProcessing(false);
        }
    }, [dispatch, navigate, team, retryCount]);

    useEffect(() => {
        if (!token) return;
        const params = new URLSearchParams(location.search);
        if (params.get("userStatus") === "new") return;

        authenticateUser(token);
    }, [token, location.search, authenticateUser]);

    const handleRetry = () => {
        setRetryCount(0);
        setError(null);
        if (token) {
            authenticateUser(token);
        } else {
            navigate("/login", { replace: true });
        }
    };

    const clearCache = () => {
        localStorage.clear();
        sessionStorage.clear();
        caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
        localforage.clear();
        window.location.reload();
    };

    if (isProcessing && !error) {
        return (
            <Div fluid flex centered ht="100vh">
                <Loader active size="large">
                    {t("auth.loginPage.loggingIn")}
                    {retryCount > 0 && ` (${t("auth.loginPage.retrying")} ${retryCount})`}
                </Loader>
            </Div>
        );
    }

    return (
        <AuthLayout title={`Login to ${t("appName")}`} team={team} teamLogin={true}>
            <Div fluid superpad ht={isPortrait ? wPageHt : "100%"} flex centered maxWd="480px">
                <Div wd="60%" gutter>
                    <Image src={branding?.image} />
                </Div>
                <Div fluid white fullHt superpad rounded>
                    {error ? (
                        <Div center-align large>
                            <Message negative>
                                <Message.Header>{t("auth.loginPage.error")}</Message.Header>
                                <p>{error}</p>
                            </Message>
                            <Button
                                primary
                                onClick={handleRetry}
                                content={t("auth.loginPage.retry")}
                                loading={isProcessing}
                            />
                            <Divider />
                            <Button
                                secondary
                                onClick={clearCache}
                                content={t("auth.loginPage.clearCache")}
                            />
                        </Div>
                    ) : null}
                    <Divider />
                    <Div txtMain center-align clickable onClick={() => {
                        setToken(null);
                        localStorage.removeItem("token");
                        localStorage.removeItem("redirectPath");
                        navigate("/login", { replace: true });
                    }}>
                        {t(`auth.authLinks.upass`)}
                    </Div>
                    {authPageLinks}
                </Div>
            </Div>
        </AuthLayout>
    );
}
